import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

export const BlogPostFragment = gql`
  fragment BlogPostFragment on BlogPost {
    id
    postId
    slug
    title
    abstract
    image
    tags
    authorId
    date
    content
    status
    editedAt
    publishedAt
    publishedSlug
    createdAt
    updatedAt
  }
`;

// The listing leaves `content` out: a delta is unbounded and the listing
// never shows it. The same split the sections listing makes.
export const BlogPostListingFragment = gql`
  fragment BlogPostListingFragment on BlogPost {
    id
    postId
    slug
    title
    tags
    authorId
    date
    status
    publishedAt
    publishedSlug
    createdAt
    updatedAt
  }
`;

export const list = gql`
  query BlogPostList($siteId: String!, $input: ListInput) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            blog {
              id
              posts {
                id
                list(input: $input) {
                  id
                  last
                  items {
                    ...BlogPostListingFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${BlogPostListingFragment}
`;

export const search = gql`
  query BlogPostSearch($siteId: String!, $input: SearchInput!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            blog {
              id
              posts {
                id
                search(input: $input) {
                  id
                  last
                  items {
                    ...BlogPostListingFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${BlogPostListingFragment}
`;

export const get = gql`
  query BlogPostGet($siteId: String!, $postId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            blog {
              id
              posts {
                id
                get(postId: $postId) {
                  ...BlogPostFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${BlogPostFragment}
`;

export const create = gql`
  mutation BlogPostNew($siteId: String!, $input: BlogPostInputCreate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            blog {
              posts {
                create(input: $input) {
                  ...BlogPostFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${BlogPostFragment}
`;

export const edit = gql`
  mutation BlogPostUpdate($siteId: String!, $postId: String!, $input: BlogPostInputUpdate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            blog {
              posts {
                get(postId: $postId) {
                  update(input: $input) {
                    ...BlogPostFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${BlogPostFragment}
`;

export const publish = gql`
  mutation BlogPostPublish($siteId: String!, $postId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            blog {
              posts {
                get(postId: $postId) {
                  publish {
                    ...BlogPostFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${BlogPostFragment}
`;

export const unpublish = gql`
  mutation BlogPostUnpublish($siteId: String!, $postId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            blog {
              posts {
                get(postId: $postId) {
                  unpublish {
                    ...BlogPostFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${BlogPostFragment}
`;

export const remove = gql`
  mutation BlogPostRemove($siteId: String!, $postId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            blog {
              posts {
                get(postId: $postId) {
                  remove
                }
              }
            }
          }
        }
      }
    }
  }
`;

// A write to a draft is a write to the drafts listing. Publish and unpublish
// are writes to the PUBLISHED listing too — every picker and preview that
// reads it is one namespace over, so both keys go.
export const update = () => (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`blog/posts/queries`) || key.includes(`blog/published/queries`)) {
      cache.data.delete(key);
    }
  });
};
